import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { apiTokenRequest, isAzureAdConfigured } from '../auth/msalConfig';
import { msalInstance } from '../auth/msalInstance';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Capture as a local non-null const so TS narrowing carries into the async
// interceptor closures below.
const msal = isAzureAdConfigured ? msalInstance : null;

if (msal) {
  api.interceptors.request.use(async (config) => {
    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) {
      return config;
    }

    try {
      const response = await msal.acquireTokenSilent({
        ...apiTokenRequest,
        account: accounts[0],
      });
      config.headers.Authorization = `Bearer ${response.accessToken}`;

      // Identity hint headers were removed deliberately: the API resolves the acting
      // user from signed token claims only. Client-supplied identity is spoofable, and
      // Deficits stamps it into Payroll_Timesheets.WhoLastChanged. If the API can't see
      // an email, add the `email`/`upn` optional claims to the access token in Entra.
    } catch (err) {
      // Silent acquisition can fail if the user's session expired or consent is
      // missing for this scope. Force a fresh login redirect so we get a valid token.
      if (err instanceof InteractionRequiredAuthError) {
        await msal.acquireTokenRedirect(apiTokenRequest);
      }
    }

    return config;
  });
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (msal && error.response?.status === 401) {
      // Token may be valid but expired or rejected — trigger re-auth.
      await msal.acquireTokenRedirect(apiTokenRequest);
    }
    return Promise.reject(error);
  }
);

// Acquire an API access token outside the Axios pipeline — used by the SignalR client, whose
// accessTokenFactory needs a raw token string. Mirrors the request interceptor above.
export const acquireAccessToken = async (): Promise<string> => {
  if (!msal) return '';
  const accounts = msal.getAllAccounts();
  if (accounts.length === 0) return '';
  try {
    const response = await msal.acquireTokenSilent({ ...apiTokenRequest, account: accounts[0] });
    return response.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msal.acquireTokenRedirect(apiTokenRequest);
    }
    return '';
  }
};

export default api;
